(() => {
  const messages = JSON.parse(document.getElementById('FairfTranslations')?.textContent || '{}');
  const t = (key, values = {}) => String(messages[key] || key).replace(/\{(\w+)\}/g, (_, name) => values[name] ?? '');
  const locale = document.documentElement.lang === 'en' ? 'en-GB' : document.documentElement.lang || 'nl-NL';
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const chosenInput = host => host?.querySelector('input[data-option-name="Kleur"]:checked');
  const chosenName = host => chosenInput(host)?.dataset.fairfColorName || chosenInput(host)?.value || '';
  const collectionName = value => value === '24 tinten wit' ? t('white_collection') : value === 'Standaard wit' ? t('standard_white') : value;
  const sources = new Map();
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const readColors = url => {
    if (!sources.has(url)) sources.set(url, fetch(url).then(r => {
      if (!r.ok) throw new Error(t('load_error'));
      return r.json();
    }));
    return sources.get(url);
  };
  const colorOf = (colors, name) => colors.find(c => normalize(c.name) === normalize(name) || normalize(c.code) === normalize(name));
  const safeHex = value => /^#[a-f0-9]{6}$/i.test(value || '') ? value : '#ddd';
  const number = value => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);

  class FairfPalette extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        const groupKey = value => ['grijs', 'zwart'].includes(value) ? 'grijs-zwart' : normalize(value) === 'creme' ? 'cream' : value;
        this.colors = (await readColors(this.dataset.source)).filter(c => c.code !== 'ANYCOLOUR').map(color => ({ ...color, group: groupKey(color.group) }));
        this.samples = this.dataset.samples ? await readColors(this.dataset.samples) : null;
        this.sampleOnly = this.hasAttribute('data-sample-only');
        this.sampleHandles = new Map(Object.entries(this.samples || {}).map(([name, handle]) => [normalize(name), handle]));
        if (this.sampleOnly) this.colors = this.colors.filter(color => this.sampleHandles.has(normalize(color.name)));
        this.search = this.querySelector('[data-color-search]');
        this.collection = this.querySelector('[data-color-collection]');
        this.group = this.querySelector('[data-color-group]');
        [...new Set(this.colors.map(color => color.collection).filter(Boolean))].forEach(value => this.collection.add(new Option(collectionName(value), value)));
        this.querySelectorAll('[data-color-family]').forEach(button => {
          const value = button.dataset.colorFamily;
          if (value) this.group.add(new Option(button.textContent.trim(), value));
        });
        this.initialLimit = Number(this.dataset.initialLimit) || 12;
        this.limit = this.initialLimit;
        if (this.hasAttribute('data-full-page')) {
          const params = new URL(location.href).searchParams;
          this.search.value = params.get('kleur') || '';
          if ([...this.collection.options].some(option => option.value === params.get('collectie'))) this.collection.value = params.get('collectie');
          this.closest('.fairf-discovery-page')?.querySelectorAll('[data-collection-link]').forEach(link => link.addEventListener('click', event => {
            event.preventDefault();
            this.search.value = '';
            this.group.value = '';
            this.collection.value = link.dataset.collectionLink;
            this.limit = this.initialLimit;
            this.render();
            this.querySelector('[data-color-collection]').focus({ preventScroll: true });
            this.closest('section').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          }));
        }
        const refresh = () => { this.limit = this.initialLimit; this.render(); };
        this.search.addEventListener('input', refresh);
        this.collection.addEventListener('change', refresh);
        this.group.addEventListener('change', refresh);
        this.querySelectorAll('[data-color-family]').forEach(button => button.addEventListener('click', () => this.chooseGroup(button.dataset.colorFamily)));
        this.querySelector('[data-more]').addEventListener('click', () => {
          const previous = this.limit;
          this.limit += 48;
          this.render();
          this.querySelectorAll('.fairf-color-card')[previous]?.focus({ preventScroll: true });
        });
        this.render();
      } catch (error) { this.querySelector('[data-color-count]').textContent = error.message; }
    }
    chooseGroup(group) {
      if (!this.colors || ![...this.group.options].some(option => option.value === group)) return;
      this.search.value = '';
      this.collection.value = '';
      this.group.value = group;
      this.limit = this.initialLimit;
      this.render();
    }
    render() {
      const term = normalize(this.search.value);
      this.querySelectorAll('[data-color-family]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.colorFamily === this.group.value)));
      const colors = this.colors.filter(c => (!term || normalize(`${c.name} ${c.code}`).includes(term)) && (!this.collection.value || c.collection === this.collection.value) && (!this.group.value || c.group === this.group.value));
      const fragment = document.createDocumentFragment();
      for (const color of colors.slice(0, this.limit)) {
        const link = document.createElement('a');
        link.className = 'fairf-color-card';
        if (!this.samples) link.dataset.previewColour = color.name;
        const sampleHandle = this.sampleHandles.get(normalize(color.name));
        const paintHref = `${this.samples && color.paintHandle ? root + 'products/' + color.paintHandle : this.dataset.productUrl}?kleur=${encodeURIComponent(color.name)}`;
        link.href = sampleHandle && (this.sampleOnly || color.paintHandle === null) ? `${root}products/${sampleHandle}` : paintHref;
        link.style.setProperty('--swatch', safeHex(color.hex));
        const swatch = document.createElement('i'); swatch.setAttribute('aria-hidden', 'true');
        const name = document.createElement('strong'); name.textContent = color.name;
        const collection = document.createElement('small'); collection.textContent = collectionName(color.collection);
        link.append(swatch, name, collection);
        if (this.samples) {
          const card = document.createElement('div'); card.className = 'fairf-colour-item'; card.dataset.previewColour = color.name;
          const actions = document.createElement('div'); actions.className = 'fairf-colour-actions';
          const paint = document.createElement('a'); paint.href = color.paintHandle === null ? `${root}pages/contact` : paintHref; paint.textContent = t(color.paintHandle === null ? 'paint_advice' : 'discovery_paint'); paint.setAttribute('aria-label', `${paint.textContent}: ${color.name}`);
          const sample = document.createElement(sampleHandle ? 'a' : 'span');
          if (sampleHandle) { sample.href = `${root}products/${sampleHandle}`; sample.textContent = t('discovery_sample'); sample.setAttribute('aria-label', `${sample.textContent}: ${color.name}`); }
          else { sample.textContent = t('discovery_no_sample'); sample.className = 'fairf-no-sample'; }
          actions.append(paint, sample); card.append(link, actions); fragment.append(card);
        } else fragment.append(link);
      }
      this.querySelector('[data-color-grid]').replaceChildren(fragment);
      this.querySelector('[data-color-count]').textContent = colors.length ? t('palette_count', { count: colors.length, shown: Math.min(colors.length, this.limit) }) : t('no_colours');
      this.querySelector('[data-more]').hidden = colors.length <= this.limit;
      if (this.hasAttribute('data-full-page')) {
        const url = new URL(location.href);
        for (const [key, value] of [['kleur', this.search.value], ['collectie', this.collection.value]]) {
          if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
        }
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
    }
  }

  document.querySelectorAll('.fairf-homes').forEach(section => {
    const track = section.querySelector('.fairf-homes-track');
    const previous = section.querySelector('[data-rail-prev]');
    const next = section.querySelector('[data-rail-next]');
    const update = () => { previous.disabled = track.scrollLeft < 2; next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2; };
    const move = direction => track.scrollBy({ left: direction * (track.querySelector('figure').getBoundingClientRect().width + 16), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    update();
  });
  document.querySelectorAll('.fairf-inspiration-hub').forEach(hub => {
    hub.querySelectorAll('[data-story-filter]').forEach(button => button.addEventListener('click', () => {
      const category = button.dataset.storyFilter;
      let count = 0;
      hub.querySelectorAll('[data-story-category]').forEach(card => { card.hidden = Boolean(category && card.dataset.storyCategory !== category); if (!card.hidden) count++; });
      hub.querySelectorAll('[data-story-filter]').forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
      const status = hub.querySelector('[data-story-count]'); status.textContent = `${count} ${status.dataset.resultsLabel}`;
    }));
  });

  class FairfVariantColors extends HTMLElement {
    async connectedCallback() {
      const fieldset = this.closest('fieldset');
      const search = this.querySelector('input[type=search]');
      const items = [...fieldset.querySelectorAll('.fairf-color-option')];
      const anyInput = items.map(item => item.querySelector('input')).find(input => input.dataset.fairfColorCode === 'ANYCOLOUR' || normalize(input.value).replace(/\s/g, '') === 'anycolour');
      const anyButton = this.querySelector('[data-any-select]');
      const collection = this.querySelector('[data-variant-collection]');
      this.querySelector('[role=status]').textContent = t('colour_count', { count: items.length - (anyInput ? 1 : 0) });
      if (anyInput && anyButton) {
        anyButton.hidden = false;
        anyButton.setAttribute('aria-pressed', String(anyInput.checked));
        anyInput.closest('.fairf-color-option').hidden = true;
        anyButton.addEventListener('click', () => {
          const selector = fieldset.closest('product-info').querySelector('fairf-paint-selector');
          if (selector?.pending?.has('Kleur')) selector.confirm('Kleur');
          if (!anyInput.checked) { anyInput.checked = true; anyInput.dispatchEvent(new Event('change', { bubbles: true })); }
          fieldset.closest('product-info').querySelector('[data-any-fields]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      }
      search.addEventListener('change', event => event.stopPropagation());
      const filterColours = () => {
        let visible = 0;
        items.forEach(item => { item.hidden = item.contains(anyInput) || !normalize(item.textContent + ' ' + (item.dataset.code || '')).includes(normalize(search.value)) || Boolean(collection?.value && item.dataset.collection !== collection.value); if (!item.hidden) visible++; });
        this.querySelector('[role=status]').textContent = visible === 1 ? t('one_colour') : t('colour_count', { count: visible });
      };
      search.addEventListener('input', filterColours);
      collection?.addEventListener('change', event => { event.stopPropagation(); filterColours(); });
      try {
        const colors = await readColors(this.dataset.source);
        items.forEach(item => {
          const input = item.querySelector('input');
          const color = colorOf(colors, input.dataset.fairfColorCode || input.value);
          if (color) {
            item.style.setProperty('--swatch', safeHex(color.hex)); item.dataset.code = color.code; item.dataset.collection = color.collection || '';
            input.dataset.fairfColorCode = color.code; input.dataset.fairfColorName = color.name;
            const label = item.querySelector('label > span:not(.label-unavailable)');
            if (label && label.textContent !== color.name) label.textContent = color.name;
            if (input.checked) fieldset.querySelector('.fairf-color-current').textContent = color.name;
          }
        });
        if (collection && items.length > 3) {
          [...new Set(items.filter(item => !item.contains(anyInput)).map(item => item.dataset.collection).filter(Boolean))].forEach(value => collection.add(new Option(collectionName(value), value)));
          collection.closest('label').hidden = collection.options.length < 3;
        }
        filterColours();
        const container = fieldset.querySelector('.fairf-color-options');
        const selected = fieldset.querySelector('input:checked')?.closest('.fairf-color-option');
        if (container && selected) container.scrollTop = Math.max(0, selected.offsetTop - container.offsetTop);
      } catch { /* Native option names stay available if the decorative color data fails. */ }
    }
  }


  class FairfPaintStudio extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-studio-track]');
      this.tabs = [...this.querySelectorAll('[data-studio-tab]')];
      this.panels = [...this.querySelectorAll('.fairf-studio-panel')];
      this.abort = new AbortController();
      const listen = (node, event, fn) => node.addEventListener(event, fn, { signal: this.abort.signal });
      this.tabs.forEach((tab, index) => listen(tab, 'click', () => this.go(index)));
      listen(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.panels.length - 1 : this.index() + (event.key === 'ArrowRight' ? 1 : -1));
      });
      listen(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.tabs.forEach((tab, index) => tab.setAttribute('aria-pressed', String(index === this.index()))));
      });
    }
    index() { return Math.round(this.track.scrollLeft / this.track.clientWidth); }
    go(index) { this.track.scrollTo({ left: Math.max(0, Math.min(this.panels.length - 1, index)) * this.track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }); }
    disconnectedCallback() { this.abort?.abort(); cancelAnimationFrame(this.frame); }
  }

  class FairfPaintSelector extends HTMLElement {
    connectedCallback() {
      this.host = this.closest('product-info');
      this.catalog = JSON.parse(this.querySelector('[data-paint-catalog]').textContent);
      this.links = [...this.querySelectorAll('[data-paint-target]')];
      this.abort = new AbortController();
      const listen = (node, event, fn, extra = {}) => node.addEventListener(event, fn, { signal: this.abort.signal, ...extra });
      const params = new URL(location.href).searchParams;
      this.fromColour = this.dataset.family === 'sample' || params.has('kleur') || params.has('vorige_kleur');
      this.pending = new Map();
      if (params.get('vorige_kleur')) this.pending.set('Kleur', params.get('vorige_kleur'));
      if (params.get('vorige_inhoud')) this.pending.set('Verpakking', params.get('vorige_inhoud'));
      listen(this.host, 'change', event => { if (event.target.matches('input[data-option-name="Kleur"]')) this.fromColour = true; this.sync(); });
      listen(this.host, 'click', event => {
        const input = event.target.closest('input[data-option-name]');
        if (input && event.isTrusted) this.confirm(input.dataset.optionName);
      });
      listen(document, 'submit', event => {
        if (!this.pending.size || !this.host.contains(event.target)) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.querySelector('[data-choice-note]').focus();
      }, { capture: true });
      this.querySelector('[data-choice-confirm]')?.addEventListener('click', () => this.confirm(), { signal: this.abort.signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.host, { childList: true, subtree: true });
      this.links.forEach(link => listen(link, 'click', event => {
        if (link.getAttribute('aria-disabled') === 'true' || link.dataset.paintTarget === this.dataset.current) {
          event.preventDefault(); return;
        }
        try {
          sessionStorage.setItem('fairf-paint-draft', JSON.stringify({
            target: link.dataset.paintTarget,
            values: [...this.host.querySelectorAll('[data-any-input]')].filter(input => !input.disabled).map(input => ({ key: input.dataset.anyKey, name: input.name, value: input.value }))
          }));
        } catch (_) { /* Direct entry remains available. */ }
      }));
      this.sync();
    }
    confirm(option) {
      if (option) this.pending.delete(option); else this.pending.clear();
      const url = new URL(location.href);
      if (!this.pending.has('Kleur')) url.searchParams.delete('vorige_kleur');
      if (!this.pending.has('Verpakking')) url.searchParams.delete('vorige_inhoud');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      this.sync();
    }
    sync() {
      const color = chosenName(this.host) || this.catalog[this.dataset.current]?.fixedColour || '';
      const colorCode = chosenInput(this.host)?.dataset.fairfColorCode || '';
      const pack = this.host.querySelector('input[data-option-name="Verpakking"]:checked')?.value || '';
      const switchColor = this.pending.get('Kleur') || color;
      const switchPack = this.pending.get('Verpakking') || pack;
      const switchCode = this.pending.has('Kleur') ? '' : colorCode;
      this.links.forEach(link => {
        const target = this.catalog[link.dataset.paintTarget];
        if (!target?.url) { link.setAttribute('aria-disabled', 'true'); return; }
        link.hidden = !window.FairfColourRules.showPaint({ handle: link.dataset.paintTarget, paint: target, current: this.dataset.current, fromColour: this.fromColour, name: switchColor, code: switchCode });
        const url = new URL(target.url, location.origin);
        if (switchColor && target.fixedColour && normalize(switchColor) !== normalize(target.fixedColour)) url.searchParams.set('vorige_kleur', switchColor);
        const ids = target.options.map(option => {
          const wanted = option.name === 'Kleur' ? switchColor : option.name === 'Verpakking' ? (link.dataset.choicePack || switchPack) : '';
          const match = option.name === 'Kleur' ? window.FairfColourRules.matchingColour(target, wanted, switchCode) : option.values.find(value => normalize(value.name).replace(',', '.') === normalize(wanted).replace(',', '.'));
          if (wanted && !match) url.searchParams.set(option.name === 'Kleur' ? 'vorige_kleur' : 'vorige_inhoud', wanted);
          if (option.name === 'Kleur' && match && switchColor) url.searchParams.set('kleur', switchColor);
          return (match || option.values[0])?.id;
        });
        if (ids.length && ids.every(Boolean)) url.searchParams.set('option_values', ids.join(','));
        const href = url.pathname + url.search;
        if (link.getAttribute('href') !== href) link.setAttribute('href', href);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('title');
      });
      const notes = [];
      if (this.pending.has('Kleur')) notes.push(t('choose_new_colour', { colour: this.pending.get('Kleur') }));
      if (this.pending.has('Verpakking')) notes.push(t('choose_new_pack', { pack: this.pending.get('Verpakking') }));
      if (this.dataset.current === 'onzichtbare-lak-mat') notes.push(t('varnish_note'));
      if (this.dataset.current === 'voorstrijk') notes.push(t('primer_note'));
      const note = this.querySelector('[data-choice-note]');
      if (note.textContent !== notes.join(' ')) note.textContent = notes.join(' ');
      this.host.toggleAttribute('data-choice-pending', this.pending.size > 0);
      const confirm = this.querySelector('[data-choice-confirm]');
      if (confirm) {
        confirm.hidden = !this.pending.size;
        const text = t('confirm_selection', { selection: [pack, color].filter(Boolean).join(' · ') });
        if (confirm.textContent !== text) confirm.textContent = text;
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
  }

  class FairfSampleOptions extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const picker = this.querySelector('[data-sample-colour]');
      try {
        const [colors, samples] = await Promise.all([readColors(this.dataset.colors), fetch(this.dataset.samples).then(r => r.json())]);
        const color = colorOf(colors, this.dataset.name);
        const input = this.querySelector('[data-option-name="Kleur"]');
        if (color) { input.dataset.fairfColorCode = color.code; input.dataset.fairfColorName = color.name; }
        const options = Object.entries(samples).sort(([a], [b]) => a.localeCompare(b, locale));
        const collection = this.querySelector('[data-sample-collection]');
        const collections = new Map(colors.map(color => [normalize(color.name), color.collection]));
        [...new Set(options.map(([name]) => collections.get(normalize(name))).filter(Boolean))].forEach(value => collection.add(new Option(collectionName(value), value)));
        const renderOptions = () => {
          const filtered = options.filter(([name]) => !collection.value || collections.get(normalize(name)) === collection.value);
          picker.replaceChildren(...filtered.map(([name, handle]) => new Option(name, handle, name === this.dataset.name, name === this.dataset.name)));
          if (!filtered.some(([name]) => name === this.dataset.name)) { const prompt = new Option(t('choose_colour'), '', true, true); prompt.disabled = true; picker.prepend(prompt); }
        };
        collection.addEventListener('change', renderOptions);
        renderOptions();
        picker.addEventListener('change', () => { location.href = `${root}products/${encodeURIComponent(picker.value)}`; });
        this.querySelector('fairf-paint-selector')?.sync();
      } catch { /* The existing sample and paint links remain usable. */ }
    }
  }

  class FairfProductTools extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.host = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      this.buybar = this.querySelector('[data-buybar]');
      this.buybarButton = this.querySelector('[data-buybar-add]');
      this.submitButton = this.form?.querySelector('[name="add"]');
      this.buybarButton.addEventListener('click', () => this.submitButton?.click());
      if (this.submitButton) {
        this.buttonObserver = new MutationObserver(() => this.syncBuybar());
        this.buttonObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.buybar.hidden = entries[0].isIntersecting;
        }, { threshold: 0.5 });
        this.visibilityObserver.observe(this.submitButton);
      }
      this.anyInputs = [...this.querySelectorAll('[data-any-input]')];
      this.colors = [];
      fetch(this.dataset.samples).then(r => r.json()).then(samples => { this.samples = samples; this.sync(); }).catch(() => {});
      this.observer = new MutationObserver(() => this.sync());
      if (this.host) this.observer.observe(this.host, { childList: true, subtree: true });
      this.host?.addEventListener('change', () => this.sync());
      this.querySelectorAll('[data-width],[data-height],[data-coats]').forEach(input => input.addEventListener('input', () => this.calculate()));
      this.querySelector('[data-use-pack]').addEventListener('click', () => this.applyPack());
      this.validate = event => {
        if (event.target !== this.form) return;
        this.sync();
        const colorName = this.selectedColor();
        if (!colorOf(this.colors, colorName)) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.host?.querySelector('product-form')?.handleErrorMessage(t('data_pending'));
          return;
        }
        const invalid = this.anyInputs.find(input => !input.disabled && (!input.value.trim() || (input.maxLength > 0 && input.value.trim().length > input.maxLength) || !input.checkValidity()));
        if (invalid) {
          event.preventDefault(); event.stopImmediatePropagation();
          const tooLong = invalid.maxLength > 0 && invalid.value.trim().length > invalid.maxLength;
          invalid.setCustomValidity(tooLong ? t('maximum', { max: invalid.maxLength }) : t('exact_colour')); invalid.reportValidity();
        }
      };
      document.addEventListener('submit', this.validate, true);
      this.anyInputs.forEach(input => input.addEventListener('input', () => {
        input.setCustomValidity('');
        try {
          sessionStorage.setItem('fairf-paint-draft', JSON.stringify({ target: this.host.querySelector('variant-selects')?.dataset.productHandle, values: this.anyInputs.map(field => ({ key: field.dataset.anyKey, value: field.value })) }));
        } catch (_) { /* Direct entry remains available. */ }
      }));
      try {
        const draft = JSON.parse(sessionStorage.getItem('fairf-paint-draft') || 'null');
        if (draft?.target === this.host.querySelector('variant-selects')?.dataset.productHandle) {
          draft.values.forEach(value => { const input = this.anyInputs.find(input => value.key ? input.dataset.anyKey === value.key : input.name === value.name); if (input) input.value = value.value; });
          sessionStorage.removeItem('fairf-paint-draft');
        }
      } catch (_) { /* A colour can always be entered directly. */ }
      this.calculate();
      try {
        this.colors = await readColors(this.dataset.colors);
        // A cached colour file can resolve before Dawn registers product-info.
        // Wait for the native variant listeners before applying a colour deep link.
        await Promise.all(['product-info', 'variant-selects'].map(name => customElements.whenDefined(name)));
        this.sync();
        const requested = new URL(location.href).searchParams.get('kleur');
        if (requested) {
          const requestedColour = colorOf(this.colors, requested);
          const input = [...this.host.querySelectorAll('input[data-option-name="Kleur"]')].find(el => requestedColour ? colorOf(this.colors, el.dataset.fairfColorCode || el.value)?.code === requestedColour.code : normalize(el.value) === normalize(requested));
          if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
          if (!input) { const selector = this.host.querySelector('fairf-paint-selector'); if (selector) { selector.pending.set('Kleur', requested); selector.sync(); } }
        }
      } catch (error) { console.error('Fairf kleurgegevens:', error); }
    }
    disconnectedCallback() { this.observer?.disconnect(); this.buttonObserver?.disconnect(); this.visibilityObserver?.disconnect(); document.removeEventListener('submit', this.validate, true); }
    selectedColor() { const input = chosenInput(this.host); return colorOf(this.colors, input?.dataset.fairfColorCode || input?.value)?.name || chosenName(this.host); }
    sync() {
      const color = colorOf(this.colors, this.selectedColor());
      const special = color?.code === 'ANYCOLOUR';
      const ecolabelNote = this.host?.querySelector('[data-any-ecolabel]');
      if (ecolabelNote) ecolabelNote.hidden = !special;
      const box = this.querySelector('[data-any-fields]');
      if (box.hidden === special) box.hidden = !special;
      this.anyInputs.forEach(input => { input.disabled = !special; input.required = special; if (!special) input.setCustomValidity(''); });
      this.querySelector('[data-color-code]').value = color?.code || '';
      const sample = Object.entries(this.samples || {}).find(([name]) => normalize(name) === normalize(this.selectedColor()));
      const sampleLink = this.querySelector('[data-matching-sample]');
      const sampleHref = sample ? `${root}products/${sample[1]}` : `${root}pages/kleurenpagina#alle-kleuren`;
      const sampleText = sample ? t('sample_named', { name: sample[0] }) : t('sample_first');
      if (sampleLink.getAttribute('href') !== sampleHref) sampleLink.setAttribute('href', sampleHref);
      if (sampleLink.textContent !== sampleText) sampleLink.textContent = sampleText;
      const packSample = this.host?.querySelector('[data-pack-sample]');
      if (packSample) {
        if (packSample.getAttribute('href') !== sampleHref) packSample.setAttribute('href', sampleHref);
        const label = sample ? t('sample_order', { name: sample[0] }) : t('sample_first');
        if (packSample.getAttribute('aria-label') !== label) packSample.setAttribute('aria-label', label);
      }
      const overlay = this.host?.querySelector('[data-selected-swatch]');
      if (overlay) {
        const show = Boolean(color && !special && !this.host.querySelector('fairf-paint-selector')?.pending?.has('Kleur'));
        overlay.hidden = !show;
        if (show) {
          overlay.style.setProperty('--selected-colour', safeHex(color.hex));
          const name = overlay.querySelector('[data-swatch-name]');
          if (name.textContent !== color.name) name.textContent = color.name;
          const sampleButton = overlay.querySelector('[data-swatch-sample]');
          sampleButton.hidden = !sample;
          if (sample) {
            sampleButton.href = sampleHref;
            sampleButton.setAttribute('aria-label', t('sample_order', { name: sample[0] }));
          }
        }
      }
      this.syncBuybar();
    }
    syncBuybar() {
      if (!this.buybar || !this.submitButton) return;
      const picker = this.host.querySelector('variant-selects');
      const variant = JSON.parse(picker?.querySelector('[data-selected-variant]')?.textContent || 'null');
      const quantity = Number(this.host.querySelector('input[name="quantity"]')?.value || 1);
      const pack = this.host.querySelector('input[data-option-name="Verpakking"]:checked')?.value || '';
      const choice = `${quantity > 1 ? quantity + ' × ' : ''}${pack} · ${this.selectedColor()}`;
      const price = variant ? new Intl.NumberFormat(locale, { style: 'currency', currency: picker.dataset.currencyCode || 'EUR' }).format(variant.price * quantity / 100) : '';
      const priceNode = this.querySelector('[data-buybar-price]');
      const choiceNode = this.querySelector('[data-buybar-choice]');
      if (priceNode.textContent !== price) priceNode.textContent = price;
      if (choiceNode.textContent !== choice) choiceNode.textContent = choice;
      this.buybarButton.disabled = this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true' || !variant;
    }
    calculate() {
      const width = Number(this.querySelector('[data-width]').value);
      const height = Number(this.querySelector('[data-height]').value);
      const coats = Number(this.querySelector('[data-coats]').value);
      const output = this.querySelector('[data-calculation]');
      if (![width, height, coats].every(n => Number.isFinite(n) && n > 0) || width > 1000 || height > 1000) {
        output.textContent = t('dimensions'); this.recommendation = null; this.querySelector('[data-use-pack]').disabled = true; return;
      }
      const liters = width * height * coats / Number(this.dataset.yield);
      const packs = [...new Set([...this.host.querySelectorAll('input[data-option-name="Verpakking"]')].map(input => parseFloat(input.value.replace(',', '.'))))].filter(n => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
      if (!packs.length) { output.textContent = t('choose_pack'); this.recommendation = null; this.querySelector('[data-use-pack]').disabled = true; return; }
      const pack = packs.find(value => value >= liters - 1e-9) || packs[packs.length - 1];
      const quantity = Math.ceil(liters / pack - 1e-9);
      this.recommendation = { pack, quantity };
      this.querySelector('[data-use-pack]').disabled = false;
      output.textContent = t('calculation', { area: number(width * height), litres: number(liters), coats, layers: t(coats === 1 ? 'coat' : 'coats'), quantity, pack: number(pack) });
    }
    applyPack() {
      if (!this.recommendation) return;
      const { pack, quantity } = this.recommendation;
      const input = [...this.host.querySelectorAll('input[data-option-name="Verpakking"]')].find(el => parseFloat(el.value.replace(',', '.')) === pack);
      const amount = this.host.querySelector('input[name="quantity"]');
      if (amount) { amount.value = quantity; amount.dispatchEvent(new Event('change', { bubbles: true })); }
      if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
      this.host.querySelector('fairf-paint-selector')?.confirm('Verpakking');
    }
  }
  for (const [name, component] of [['fairf-sample-options', FairfSampleOptions], ['fairf-paint-studio', FairfPaintStudio], ['fairf-paint-selector', FairfPaintSelector], ['fairf-palette', FairfPalette], ['fairf-variant-colors', FairfVariantColors], ['fairf-product-tools', FairfProductTools]]) {
    if (!customElements.get(name)) customElements.define(name, component);
  }
  document.querySelectorAll('.fairf-language-switch').forEach(form => {
    const returnTo = form.querySelector('[name="return_to"]');
    if (returnTo) returnTo.value = location.pathname + location.search;
    form.addEventListener('submit', () => {
      if (returnTo) returnTo.value = location.pathname + location.search;
      const host = document.querySelector('product-info');
      if (!host) return;
      try {
        sessionStorage.setItem('fairf-paint-draft', JSON.stringify({
          target: host.querySelector('variant-selects')?.dataset.productHandle,
          values: [...host.querySelectorAll('[data-any-input]')].filter(input => !input.disabled).map(input => ({ key: input.dataset.anyKey, value: input.value }))
        }));
      } catch (_) { /* Existing selection remains usable if storage is unavailable. */ }
    });
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-palette-group]');
    if (link) document.querySelector('fairf-palette')?.chooseGroup(link.dataset.paletteGroup);
    const anchor = event.target.closest('header-drawer a[href*="#fairf-"]');
    if (anchor) anchor.closest('header-drawer').querySelector('summary').click();
  });
})();
